// SLI / service-health dashboard — thin adapters over valoi_engine's authenticated
// /api/v1/admin/sli/* API (see app/routers/admin_api.py, app/db/repos.py::SliRepo).
// Same auth story as adminService.ts: every endpoint requires the caller's own
// Bearer JWT to belong to a role === 'admin' User (require_admin_user); a non-admin
// gets a 403 ApiError, same as any other admin endpoint.
//
// Every report shares one query param, `window_minutes` — these are rolling-window
// health checks ("last N minutes"), not fixed date-range reports like the rest of
// the admin API.

import { getJson } from '@/shared/api/apiClient';

const SLI_BASE = '/api/v1/admin/sli';

function windowQuery(windowMinutes: number): string {
  return `?window_minutes=${windowMinutes}`;
}

// --- Availability ------------------------------------------------------------

export type ProviderAvailability = {
  provider: string;
  total: number;
  successful: number;
  availabilityPct: number | null;
};

export type AvailabilityReport = {
  since: string;
  providers: ProviderAvailability[];
};

type AvailabilityApiResponse = {
  since: string;
  providers: { provider: string; total: number; successful: number; availability_pct: number | null }[];
};

// GET /sli/availability — per-provider success rate (usage_events) over the window.
export async function getAvailability(windowMinutes: number): Promise<AvailabilityReport> {
  const body = await getJson<AvailabilityApiResponse>(`${SLI_BASE}/availability${windowQuery(windowMinutes)}`);
  return {
    since: body.since,
    providers: body.providers.map((p) => ({
      provider: p.provider,
      total: p.total,
      successful: p.successful,
      availabilityPct: p.availability_pct,
    })),
  };
}

// --- Cost-estimate latency -----------------------------------------------------

export type EstimateLatencyReport = {
  since: string;
  count: number;
  avgMs: number | null;
  p50Ms: number | null;
  p95Ms: number | null;
};

type EstimateLatencyApiResponse = {
  since: string;
  count: number;
  avg_ms: number | null;
  p50_ms: number | null;
  p95_ms: number | null;
};

// GET /sli/latency/estimate — latency of credits.estimate_rough, the SLI the
// create-video request's UX is gated on.
export async function getEstimateLatency(windowMinutes: number): Promise<EstimateLatencyReport> {
  const body = await getJson<EstimateLatencyApiResponse>(
    `${SLI_BASE}/latency/estimate${windowQuery(windowMinutes)}`,
  );
  return {
    since: body.since,
    count: body.count,
    avgMs: body.avg_ms,
    p50Ms: body.p50_ms,
    p95Ms: body.p95_ms,
  };
}

// --- GPU queue (RunPod) --------------------------------------------------------

export type GpuQueueEndpoint = {
  endpointId: string;
  inQueue: number;
  inProgress: number;
  sampledAt: string;
};

export type GpuQueueReport = {
  since: string;
  endpoints: GpuQueueEndpoint[];
  avgWaitSeconds: number | null;
  waitSampleCount: number;
};

type GpuQueueApiResponse = {
  since: string;
  endpoints: { endpoint_id: string; in_queue: number; in_progress: number; sampled_at: string }[];
  avg_wait_seconds: number | null;
  wait_sample_count: number;
};

// GET /sli/gpu-queue — latest RunPod queue depth per Wan endpoint, plus the average
// GPU-dispatch wait (SQS/RunPod queue -> first observed IN_PROGRESS) over the window.
export async function getGpuQueue(windowMinutes: number): Promise<GpuQueueReport> {
  const body = await getJson<GpuQueueApiResponse>(`${SLI_BASE}/gpu-queue${windowQuery(windowMinutes)}`);
  return {
    since: body.since,
    endpoints: body.endpoints.map((e) => ({
      endpointId: e.endpoint_id,
      inQueue: e.in_queue,
      inProgress: e.in_progress,
      sampledAt: e.sampled_at,
    })),
    avgWaitSeconds: body.avg_wait_seconds,
    waitSampleCount: body.wait_sample_count,
  };
}

// --- Queries per minute --------------------------------------------------------

export type ProviderQpm = {
  provider: string;
  count: number;
  qpm: number | null;
};

export type QpmReport = {
  since: string;
  windowMinutes: number;
  providers: ProviderQpm[];
};

type QpmApiResponse = {
  since: string;
  window_minutes: number;
  providers: { provider: string; count: number; qpm: number | null }[];
};

// GET /sli/qpm — requests/minute per generative provider (Gemini, ElevenLabs, ...).
export async function getQpm(windowMinutes: number): Promise<QpmReport> {
  const body = await getJson<QpmApiResponse>(`${SLI_BASE}/qpm${windowQuery(windowMinutes)}`);
  return {
    since: body.since,
    windowMinutes: body.window_minutes,
    providers: body.providers.map((p) => ({ provider: p.provider, count: p.count, qpm: p.qpm })),
  };
}

// --- Estimated vs. actual cost --------------------------------------------------

export type CostDeltaReport = {
  since: string;
  count: number;
  avgDeltaPct: number | null;
  p50DeltaPct: number | null;
  p95DeltaPct: number | null;
};

type CostDeltaApiResponse = {
  since: string;
  count: number;
  avg_delta_pct: number | null;
  p50_delta_pct: number | null;
  p95_delta_pct: number | null;
};

// GET /sli/cost-delta — % delta between the hold placed at confirm-time (estimate)
// and the actual settled bill, proxied from valoi-billing's WalletHold ledger.
export async function getCostDelta(windowMinutes: number): Promise<CostDeltaReport> {
  const body = await getJson<CostDeltaApiResponse>(`${SLI_BASE}/cost-delta${windowQuery(windowMinutes)}`);
  return {
    since: body.since,
    count: body.count,
    avgDeltaPct: body.avg_delta_pct,
    p50DeltaPct: body.p50_delta_pct,
    p95DeltaPct: body.p95_delta_pct,
  };
}

// --- Service errors --------------------------------------------------------------

export type ProviderErrorCount = { provider: string; failedCalls: number };
export type JobErrorCategoryCount = { category: string; count: number };
export type ExcTypeCount = { excType: string; count: number };

export type ErrorsReport = {
  since: string;
  byProvider: ProviderErrorCount[];
  byJobErrorCategory: JobErrorCategoryCount[];
  byExcType: ExcTypeCount[];
};

type ErrorsApiResponse = {
  since: string;
  by_provider: { provider: string; failed_calls: number }[];
  by_job_error_category: { category: string; count: number }[];
  by_exc_type: { exc_type: string; count: number }[];
};

// GET /sli/errors — failed provider calls by provider, job-level failures by error
// category, and unhandled 500s by exception type, over the window.
export async function getErrors(windowMinutes: number): Promise<ErrorsReport> {
  const body = await getJson<ErrorsApiResponse>(`${SLI_BASE}/errors${windowQuery(windowMinutes)}`);
  return {
    since: body.since,
    byProvider: body.by_provider.map((r) => ({ provider: r.provider, failedCalls: r.failed_calls })),
    byJobErrorCategory: body.by_job_error_category.map((r) => ({ category: r.category, count: r.count })),
    byExcType: body.by_exc_type.map((r) => ({ excType: r.exc_type, count: r.count })),
  };
}

// --- Content guardrail rate -----------------------------------------------------

export type GuardrailRateReport = {
  since: string;
  totalScenes: number;
  guardrailTriggered: number;
  guardrailPct: number | null;
};

type GuardrailRateApiResponse = {
  since: string;
  total_scenes: number;
  guardrail_triggered: number;
  guardrail_pct: number | null;
};

// GET /sli/guardrail-rate — % of scenes that hit a content-guardrail rejection.
export async function getGuardrailRate(windowMinutes: number): Promise<GuardrailRateReport> {
  const body = await getJson<GuardrailRateApiResponse>(`${SLI_BASE}/guardrail-rate${windowQuery(windowMinutes)}`);
  return {
    since: body.since,
    totalScenes: body.total_scenes,
    guardrailTriggered: body.guardrail_triggered,
    guardrailPct: body.guardrail_pct,
  };
}

// --- Scene regeneration rate -----------------------------------------------------

export type RegenerationRateReport = {
  since: string;
  totalScenes: number;
  regeneratedScenes: number;
  regenerationPct: number | null;
  avgRetriesPerJob: number | null;
};

type RegenerationRateApiResponse = {
  since: string;
  total_scenes: number;
  regenerated_scenes: number;
  regeneration_pct: number | null;
  avg_retries_per_job: number | null;
};

// GET /sli/regeneration-rate — % of scenes needing at least one retry, any reason.
export async function getRegenerationRate(windowMinutes: number): Promise<RegenerationRateReport> {
  const body = await getJson<RegenerationRateApiResponse>(
    `${SLI_BASE}/regeneration-rate${windowQuery(windowMinutes)}`,
  );
  return {
    since: body.since,
    totalScenes: body.total_scenes,
    regeneratedScenes: body.regenerated_scenes,
    regenerationPct: body.regeneration_pct,
    avgRetriesPerJob: body.avg_retries_per_job,
  };
}
